import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Edit, Trash2, ArrowLeft, DollarSign, TrendingUp, TrendingDown, Calendar, Wallet } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { FormReactSelect, Drawer, Spinner, Badge, Divider, Card, Button, Modal, FormField, Table } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';

const BrokerInvestors = () => {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [broker, setBroker] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [allInvestors, setAllInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRel, setEditingRel] = useState(null);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [investorData, setInvestorData] = useState({
    portfolio: null,
    transactions: [],
    payouts: [],
    loading: false
  });
  const [formData, setFormData] = useState({
    investor_id: '',
    roi_min: 0,
    roi_max: 0
  });

  useEffect(() => {
    fetchBrokerInvestors();
    if (currentUser?.role === 'admin') {
      fetchAllInvestors();
    }
  }, [brokerId, currentUser]);

  const fetchBrokerInvestors = async () => {
    try {
      setLoading(true);
      
      // If brokerId is in URL (admin viewing specific broker)
      if (brokerId && currentUser?.role === 'admin') {
        const [brokerRes, investorsRes] = await Promise.all([
          api.get(`/api/brokers/${brokerId}`),
          api.get(`/api/brokers/${brokerId}/investors`)
        ]);
        setBroker(brokerRes.data.broker);
        setInvestors(investorsRes.data.investors || []);
      } 
      // If user is a broker, fetch their own investors
      else if (currentUser?.role === 'broker') {
        const response = await api.get('/api/broker-investors');
        const brokerInvestors = response.data.brokerInvestors || [];
        setInvestors(brokerInvestors);
        
        // Set broker info from first relationship if available
        if (brokerInvestors.length > 0 && brokerInvestors[0].broker_id) {
          setBroker({
            name: brokerInvestors[0].broker_id.name,
            email: brokerInvestors[0].broker_id.email
          });
        } else {
          // If no relationships, set broker info from current user
          setBroker({
            name: `${currentUser.firstName} ${currentUser.lastName}`,
            email: currentUser.email
          });
        }
      }
      // Admin viewing all relationships
      else if (currentUser?.role === 'admin') {
        const response = await api.get('/api/broker-investors');
        setInvestors(response.data.brokerInvestors || []);
        setBroker({ name: 'All Brokers', email: '' });
      }
    } catch (error) {
      console.error('Error fetching broker investors:', error);
      showError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllInvestors = async () => {
    try {
      const response = await api.get('/api/users');
      const investorUsers = response.data.users.filter(u => u.role === 'investor');
      setAllInvestors(investorUsers);
    } catch (error) {
      console.error('Error fetching investors:', error);
    }
  };

  const handleOpenModal = (rel = null) => {
    if (rel) {
      setEditingRel(rel);
      setFormData({
        investor_id: rel.investor_id._id,
        roi_min: rel.roi_min || 0,
        roi_max: rel.roi_max || 0
      });
    } else {
      setEditingRel(null);
      setFormData({
        investor_id: '',
        roi_min: 0,
        roi_max: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRel(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRel) {
        await api.put(`/api/broker-investors/${editingRel._id}`, {
          roi_min: formData.roi_min,
          roi_max: formData.roi_max
        });
      } else {
        if (!brokerId) {
          showError('Broker ID is required');
          return;
        }
        await api.post('/api/broker-investors', {
          ...formData,
          broker_id: brokerId
        });
      }
      fetchBrokerInvestors();
      handleCloseModal();
      showSuccess(editingRel ? 'Relationship updated successfully' : 'Relationship created successfully');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save relationship');
    }
  };

  const handleDelete = async (relId) => {
    const confirmed = await confirm({
      title: 'Remove Investor',
      message: 'Are you sure you want to remove this investor from the broker?',
      variant: 'warning',
      confirmText: 'Remove',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/broker-investors/${relId}`);
      showSuccess('Investor removed successfully');
      fetchBrokerInvestors();
    } catch (error) {
      showError('Failed to delete relationship');
    }
  };

  const fetchInvestorDetails = async (investorId) => {
    setInvestorData({ portfolio: null, transactions: [], payouts: [], loading: true });
    try {
      // Fetch portfolio, transactions, and payouts for the investor
      const [portfolioRes, transactionsRes, payoutsRes] = await Promise.all([
        api.get(`/api/users/${investorId}/portfolio`).catch(() => ({ data: { portfolio: null } })),
        api.get(`/api/users/${investorId}/transactions?limit=50`).catch(() => ({ data: { transactions: [] } })),
        api.get(`/api/payouts?userId=${investorId}`).catch(() => ({ data: { payouts: [] } }))
      ]);

      setInvestorData({
        portfolio: portfolioRes.data.portfolio,
        transactions: transactionsRes.data.transactions || [],
        payouts: payoutsRes.data.payouts || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching investor details:', error);
      setInvestorData({ portfolio: null, transactions: [], payouts: [], loading: false });
    }
  };

  const handleInvestorClick = async (rel) => {
    setSelectedInvestor(rel);
    setShowDrawer(true);
    await fetchInvestorDetails(rel.investor_id._id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const availableInvestors = allInvestors.filter(
    inv => !investors.some(rel => rel.investor_id._id === inv._id)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {brokerId && currentUser?.role === 'admin' && (
            <button onClick={() => navigate('/brokers')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              <ArrowLeft size={20} />
            </button>
          )}
          <Users className="text-primary-600 dark:text-primary-400" size={32} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              {broker?.name ? `${broker.name} - Investors` : 'Broker Investors'}
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1 text-sm sm:text-base">
              {currentUser?.role === 'broker' ? 'View your assigned investors' : 'Manage investor relationships'}
            </p>
          </div>
        </div>
        {currentUser?.role === 'admin' && brokerId && availableInvestors.length > 0 && (
          <button onClick={() => handleOpenModal()} className="btn-primary flex items-center gap-2">
            <Plus size={20} />
            Add Investor
          </button>
        )}
      </div>

      <Card className="overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-primary-200 dark:border-primary-700">
                <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">Investor</th>
                <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">Email</th>
                <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">Invested Amount</th>
                <th className="text-left py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">ROI Range</th>
                {currentUser?.role === 'admin' && brokerId && (
                  <th className="text-center py-3 px-4 font-semibold text-primary-900 dark:text-primary-50">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {investors.map((rel) => (
                <tr
                  key={rel._id}
                  className="border-b border-primary-100 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/50 transition-colors"
                >
                  <td className="py-3 px-4 font-medium text-primary-900 dark:text-primary-50">
                    <button
                      onClick={() => handleInvestorClick(rel)}
                      className="text-left font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline transition-colors cursor-pointer"
                    >
                      {rel.investor_id?.firstName && rel.investor_id?.lastName
                        ? `${rel.investor_id.firstName} ${rel.investor_id.lastName}`
                        : rel.investor_id?.email || 'Unknown Investor'}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-primary-600 dark:text-primary-400">
                    {rel.investor_id?.email}
                  </td>
                  <td className="py-3 px-4 text-primary-600 dark:text-primary-400">
                    ${rel.investor_id?.invested_amount?.toLocaleString() || 0} USDT
                  </td>
                  <td className="py-3 px-4 text-primary-600 dark:text-primary-400">
                    {(rel.roi_min ?? 0).toFixed(1)}% - {(rel.roi_max ?? 0).toFixed(1)}%
                  </td>
                  {currentUser?.role === 'admin' && brokerId && (
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenModal(rel)}
                          className="p-2 text-warning-600 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(rel._id)}
                          className="p-2 text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded transition-colors"
                          title="Remove"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {investors.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto text-primary-400 dark:text-primary-600 mb-3" size={48} />
            <p className="text-primary-600 dark:text-primary-400 font-medium">
              {currentUser?.role === 'broker' 
                ? 'No investors assigned to you yet' 
                : 'No investors assigned to this broker'}
            </p>
          </div>
        )}
      </Card>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingRel ? 'Edit Relationship' : 'Add Investor'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingRel && (
            <FormReactSelect
              label="Investor"
              name="investor_id"
              required
              value={formData.investor_id}
              onChange={(e) => setFormData({ ...formData, investor_id: e.target.value })}
              options={availableInvestors.map((inv) => ({
                value: inv._id,
                label: inv.firstName && inv.lastName
                  ? `${inv.firstName} ${inv.lastName} - ${inv.email}`
                  : inv.email,
              }))}
              placeholder="Select an investor"
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label="Min ROI (%)"
              name="roi_min"
              type="number"
              step="0.1"
              required
              value={formData.roi_min}
              onChange={(e) => setFormData({ ...formData, roi_min: parseFloat(e.target.value) || 0 })}
            />
            <FormField
              label="Max ROI (%)"
              name="roi_max"
              type="number"
              step="0.1"
              required
              value={formData.roi_max}
              onChange={(e) => setFormData({ ...formData, roi_max: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" variant="primary" className="flex-1">
              {editingRel ? 'Update' : 'Add'}
            </Button>
            <Button type="button" onClick={handleCloseModal} variant="secondary" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Investor Details Drawer */}
      <Drawer
        isOpen={showDrawer}
        onClose={() => {
          setShowDrawer(false);
          setSelectedInvestor(null);
          setInvestorData({ portfolio: null, transactions: [], payouts: [], loading: false });
        }}
        title={selectedInvestor ? (
          <div>
            <div className="text-xl font-semibold text-primary-900 dark:text-primary-50">
              {selectedInvestor.investor_id?.firstName && selectedInvestor.investor_id?.lastName
                ? `${selectedInvestor.investor_id.firstName} ${selectedInvestor.investor_id.lastName}`
                : selectedInvestor.investor_id?.email || 'Unknown Investor'}
            </div>
            <div className="text-sm text-primary-600 dark:text-primary-400 mt-1">
              {selectedInvestor.investor_id?.email}
            </div>
          </div>
        ) : 'Investor Details'}
        size="lg"
        position="right"
      >
        {selectedInvestor && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="p-4 bg-primary-50 dark:bg-primary-900/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                      Invested Amount
                    </p>
                    <p className="text-xl font-bold text-primary-900 dark:text-primary-50 mt-1">
                      ${(selectedInvestor.investor_id?.invested_amount || 0).toLocaleString()} USDT
                    </p>
                  </div>
                  <Wallet className="text-primary-400 dark:text-primary-600" size={24} />
                </div>
              </Card>
              <Card className="p-4 bg-success-50 dark:bg-success-900/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-success-600 dark:text-success-400 uppercase tracking-wide">
                      ROI Range
                    </p>
                    <p className="text-xl font-bold text-success-900 dark:text-success-50 mt-1">
                      {(selectedInvestor.roi_min ?? 0).toFixed(1)}% - {(selectedInvestor.roi_max ?? 0).toFixed(1)}%
                    </p>
                  </div>
                  <TrendingUp className="text-success-400 dark:text-success-600" size={24} />
                </div>
              </Card>
            </div>

            {investorData.portfolio && (
              <>
                <Divider />
                <div>
                  <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                    Portfolio Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                        Total Value
                      </label>
                      <p className="text-lg font-semibold text-primary-900 dark:text-primary-50 mt-1">
                        ${(investorData.portfolio.totalValue || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                        Total Invested
                      </label>
                      <p className="text-lg font-semibold text-primary-900 dark:text-primary-50 mt-1">
                        ${(investorData.portfolio.totalInvested || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                        Gain/Loss
                      </label>
                      <p className={`text-lg font-semibold mt-1 ${
                        (investorData.portfolio.totalGainLoss || 0) >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                      }`}>
                        ${(investorData.portfolio.totalGainLoss || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-primary-600 dark:text-primary-400">
                        Gain/Loss %
                      </label>
                      <p className={`text-lg font-semibold mt-1 ${
                        (investorData.portfolio.totalGainLossPercent || 0) >= 0
                          ? 'text-success-600 dark:text-success-400'
                          : 'text-error-600 dark:text-error-400'
                      }`}>
                        {(investorData.portfolio.totalGainLossPercent || 0).toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Divider />

            {/* Timeline */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Investment & Earnings Timeline
              </h3>
              {investorData.loading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="sm" />
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    // Combine transactions and payouts into a timeline
                    const timelineItems = [
                      ...investorData.transactions.map(tx => ({
                        type: 'transaction',
                        date: tx.date || tx.createdAt,
                        data: tx,
                        label: tx.transactionType === 'deposit' ? 'Investment' : 'Withdrawal',
                        amount: tx.amount,
                        isPositive: tx.transactionType === 'deposit'
                      })),
                      ...investorData.payouts.map(payout => ({
                        type: 'payout',
                        date: payout.date || payout.createdAt,
                        data: payout,
                        label: 'Payout',
                        amount: payout.amt || payout.amount,
                        isPositive: true
                      }))
                    ].sort((a, b) => new Date(b.date) - new Date(a.date));

                    if (timelineItems.length === 0) {
                      return (
                        <div className="text-center py-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg">
                          <Calendar size={32} className="mx-auto text-primary-400 dark:text-primary-600 mb-2" />
                          <p className="text-sm text-primary-600 dark:text-primary-400">
                            No activity yet
                          </p>
                        </div>
                      );
                    }

                    return timelineItems.map((item, index) => (
                      <div key={index} className="relative pl-8 pb-4">
                        {/* Timeline line */}
                        {index < timelineItems.length - 1 && (
                          <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-primary-200 dark:bg-primary-700" />
                        )}
                        {/* Timeline dot */}
                        <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          item.isPositive
                            ? 'bg-success-100 dark:bg-success-900/30 border-success-400 dark:border-success-600'
                            : 'bg-error-100 dark:bg-error-900/30 border-error-400 dark:border-error-600'
                        }`}>
                          {item.type === 'transaction' ? (
                            item.data.transactionType === 'deposit' ? (
                              <TrendingUp size={12} className="text-success-600 dark:text-success-400" />
                            ) : (
                              <TrendingDown size={12} className="text-error-600 dark:text-error-400" />
                            )
                          ) : (
                            <DollarSign size={12} className="text-success-600 dark:text-success-400" />
                          )}
                        </div>
                        {/* Content */}
                        <div className="bg-primary-50 dark:bg-primary-900/50 rounded-lg p-3 border border-primary-200 dark:border-primary-700">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={item.isPositive ? 'success' : 'error'} className="text-xs">
                                  {item.label}
                                </Badge>
                                <span className="text-xs text-primary-500 dark:text-primary-400">
                                  {new Date(item.date).toISOString().split('T')[0]}
                                </span>
                              </div>
                              {item.data.description && (
                                <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                                  {item.data.description}
                                </p>
                              )}
                              {item.type === 'payout' && item.data.admin_name && (
                                <p className="text-xs text-primary-500 dark:text-primary-400 mt-1">
                                  Processed by: {item.data.admin_name}
                                </p>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <p className={`text-lg font-bold flex items-center gap-1 ${
                                item.isPositive
                                  ? 'text-success-600 dark:text-success-400'
                                  : 'text-error-600 dark:text-error-400'
                              }`}>
                                {item.isPositive ? '+' : '-'}
                                <DirhamSymbol size={18} inline />
                                {item.amount?.toLocaleString() || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>

            <Divider />

            {/* Additional Details */}
            <div>
              <h3 className="text-sm font-semibold text-primary-500 dark:text-primary-400 uppercase tracking-wide mb-4">
                Additional Details
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-primary-200 dark:border-primary-700">
                  <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Balance (AED)
                  </span>
                  <span className="text-sm font-semibold text-primary-900 dark:text-primary-50 flex items-center gap-1">
                    <DirhamSymbol size={16} inline />
                    {(selectedInvestor.investor_id?.balance_usdt || 0).toLocaleString()}
                  </span>
                </div>
                {selectedInvestor.investor_id?.payout_date && (
                  <div className="flex items-center justify-between py-2 border-b border-primary-200 dark:border-primary-700">
                    <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      Next Payout Date
                    </span>
                    <span className="text-sm font-semibold text-primary-900 dark:text-primary-50">
                      {new Date(selectedInvestor.investor_id.payout_date).toISOString().split('T')[0]}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                    Status
                  </span>
                  <Badge variant={selectedInvestor.investor_id?.active !== false ? 'success' : 'default'}>
                    {selectedInvestor.investor_id?.active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default BrokerInvestors;
